// src/operator/console/tenant-object-vault.tsx
//
// Sous-onglet « VOLT » (credential vault) du workspace de paramétrage
// client/site. Liste, crée, modifie et révoque les credentials rattachés
// au client ou au site courant. Les secrets ne transitent ni ne s'affichent
// en clair : seul le backend les chiffre au repos (AES-256-GCM).
//
// Réutilise le client TS credential-vault-client.ts et les routes backend
// /api/credential-vault/* étendues (filtres clientId/siteId, PATCH, DELETE, ACL).

import { createSignal, createResource, Show, For, type Component } from "solid-js";
import { useNotify } from "../shared/notify";
import { useConfirm } from "../shared/confirm-dialog";
import {
  listCredentialVaultCredentials,
  createCredentialVaultCredential,
  updateCredentialVaultCredential,
  revokeCredentialVaultCredential,
  type CredentialVaultCredential,
} from "../shared/credential-vault-client";

interface Props {
  objectType: "client" | "site";
  objectId: string;
}

const KIND_OPTIONS = ["rdp", "generic", "ldap", "webapp", "ssh", "api-key", "database"] as const;

export const TenantObjectVault: Component<Props> = (props) => {
  const notify = useNotify();
  const confirmDialog = useConfirm();

  const filter = () => (props.objectType === "client" ? { clientId: props.objectId } : { siteId: props.objectId });

  const [creds, { refetch }] = createResource(async () => {
    const res = await listCredentialVaultCredentials(filter());
    return res.items;
  });
  const [search, setSearch] = createSignal("");

  const filteredCreds = () => {
    const q = search().trim().toLowerCase();
    const all = creds() ?? [];
    if (!q) return all;
    return all.filter(
      (c) =>
        (c.label ?? "").toLowerCase().includes(q) ||
        (c.username ?? "").toLowerCase().includes(q) ||
        (c.domain ?? "").toLowerCase().includes(q),
    );
  };

  // ── Formulaire de création ──
  const [showCreate, setShowCreate] = createSignal(false);
  const [form, setForm] = createSignal({
    label: "",
    username: "",
    domain: "",
    kind: "generic" as string,
    password: "",
  });
  const [creating, setCreating] = createSignal(false);

  function resetForm() {
    setForm({ label: "", username: "", domain: "", kind: "generic", password: "" });
  }

  async function create() {
    const f = form();
    if (creating()) return;
    if (!f.label.trim() || !f.username.trim() || !f.password) {
      notify.warn("Label, identifiant et mot de passe requis");
      return;
    }
    setCreating(true);
    try {
      await createCredentialVaultCredential({
        kind: f.kind,
        label: f.label.trim(),
        username: f.username.trim(),
        domain: f.domain.trim() || null,
        password: f.password,
        clientId: props.objectType === "client" ? props.objectId : null,
        siteId: props.objectType === "site" ? props.objectId : null,
      });
      resetForm();
      setShowCreate(false);
      await refetch();
      notify.ok("Credential enregistré", { hint: "Chiffré au repos (AES-256-GCM)", durationMs: 3000 });
    } catch (err) {
      notify.danger("Échec de l'enregistrement", { hint: err instanceof Error ? err.message : String(err) });
    } finally {
      setCreating(false);
    }
  }

  // ── Édition en ligne ──
  const [editingId, setEditingId] = createSignal<string | null>(null);
  const [editForm, setEditForm] = createSignal({ label: "", username: "", domain: "", kind: "generic" });
  const [editPassword, setEditPassword] = createSignal("");
  const [saving, setSaving] = createSignal(false);

  function startEdit(c: CredentialVaultCredential) {
    setEditingId(c.id);
    setEditForm({
      label: c.label ?? "",
      username: c.username ?? "",
      domain: c.domain ?? "",
      kind: c.kind ?? "generic",
    });
    setEditPassword("");
  }

  async function saveEdit(id: string) {
    if (saving()) return;
    setSaving(true);
    try {
      await updateCredentialVaultCredential(id, {
        label: editForm().label.trim(),
        username: editForm().username.trim(),
        domain: editForm().domain.trim() || null,
        kind: editForm().kind,
        password: editPassword() || undefined,
      });
      setEditingId(null);
      setEditPassword("");
      await refetch();
      notify.ok("Credential mis à jour", { durationMs: 2500 });
    } catch (err) {
      notify.danger("Échec de la mise à jour", { hint: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  }

  async function revoke(c: CredentialVaultCredential) {
    const ok = await confirmDialog.confirm({
      title: "Révoquer ce credential ?",
      body: `« ${c.label} » (${c.username}) sera supprimé (soft delete). Cette action est irréversible.`,
      confirmLabel: "Révoquer",
      level: "danger",
    });
    if (!ok) return;
    try {
      await revokeCredentialVaultCredential(c.id);
      await refetch();
      notify.ok("Credential révoqué", { durationMs: 2500 });
    } catch (err) {
      notify.danger("Échec de la révocation", { hint: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <section class="tenant-section tenant-volt">
      <div class="tenant-volt__head">
        <div>
          <h3>🔑 VOLT — Coffre de credentials</h3>
          <p class="tenant-block__hint">
            Mots de passe et secrets rattachés à ce {props.objectType === "client" ? "client" : "site"}. Chiffrés au
            repos (AES-256-GCM), jamais affichés en clair.
          </p>
        </div>
        <button class="btn btn--primary btn--sm" onClick={() => setShowCreate((v) => !v)}>
          {showCreate() ? "Annuler" : "+ Nouveau credential"}
        </button>
      </div>

      {/* ── Formulaire de création ── */}
      <Show when={showCreate()}>
        <div class="tenant-volt__form">
          <div class="tenant-volt__row">
            <label>
              <span>Label</span>
              <input
                value={form().label}
                onInput={(e) => setForm((f) => ({ ...f, label: e.currentTarget.value }))}
                placeholder="Ex : Admin local AD"
              />
            </label>
            <label>
              <span>Type</span>
              <select value={form().kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.currentTarget.value }))}>
                <For each={[...KIND_OPTIONS]}>{(k) => <option value={k}>{k}</option>}</For>
              </select>
            </label>
          </div>
          <div class="tenant-volt__row">
            <label>
              <span>Identifiant</span>
              <input
                value={form().username}
                onInput={(e) => setForm((f) => ({ ...f, username: e.currentTarget.value }))}
                placeholder="Ex : administrateur"
              />
            </label>
            <label>
              <span>Domaine (optionnel)</span>
              <input
                value={form().domain}
                onInput={(e) => setForm((f) => ({ ...f, domain: e.currentTarget.value }))}
                placeholder="Ex : ACME.LOCAL"
              />
            </label>
          </div>
          <label class="tenant-volt__full">
            <span>Mot de passe / secret</span>
            <input
              type="password"
              value={form().password}
              onInput={(e) => setForm((f) => ({ ...f, password: e.currentTarget.value }))}
              placeholder="Sera chiffré côté serveur"
            />
          </label>
          <div class="tenant-volt__form-actions">
            <button class="btn btn--primary btn--sm" onClick={create} disabled={creating()}>
              {creating() ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button class="btn btn--sm" onClick={() => setShowCreate(false)}>
              Annuler
            </button>
          </div>
        </div>
      </Show>

      {/* ── Recherche ── */}
      <div class="tenant-volt__search">
        <input
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder="Rechercher un credential…"
        />
      </div>

      {/* ── Liste ── */}
      <table class="rmm-table tenant-volt-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Type</th>
            <th>Identifiant</th>
            <th>Domaine</th>
            <th>Créé</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <For
            each={filteredCreds()}
            fallback={
              <tr>
                <td colspan={6} class="tenant-empty-cell">
                  {creds.loading ? "Chargement…" : "Aucun credential. Cliquez sur « Nouveau credential »."}
                </td>
              </tr>
            }
          >
            {(c) => (
              <Show
                when={editingId() === c.id}
                fallback={
                  <tr>
                    <td>
                      <strong>{c.label}</strong>
                    </td>
                    <td>
                      <span class="tag tag--kind">{c.kind}</span>
                    </td>
                    <td>
                      <code>{c.username}</code>
                    </td>
                    <td>{c.domain || "—"}</td>
                    <td>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}</td>
                    <td class="tenant-volt__actions">
                      <button class="btn btn--xs" onClick={() => startEdit(c)}>
                        Éditer
                      </button>
                      <button class="btn btn--xs btn--danger" onClick={() => revoke(c)}>
                        Révoquer
                      </button>
                    </td>
                  </tr>
                }
              >
                {/* Ligne d'édition en ligne */}
                <tr class="tenant-volt__edit-row">
                  <td>
                    <input
                      value={editForm().label}
                      onInput={(e) => setEditForm((f) => ({ ...f, label: e.currentTarget.value }))}
                    />
                  </td>
                  <td>
                    <select
                      value={editForm().kind}
                      onChange={(e) => setEditForm((f) => ({ ...f, kind: e.currentTarget.value }))}
                    >
                      <For each={[...KIND_OPTIONS]}>{(k) => <option value={k}>{k}</option>}</For>
                    </select>
                  </td>
                  <td>
                    <input
                      value={editForm().username}
                      onInput={(e) => setEditForm((f) => ({ ...f, username: e.currentTarget.value }))}
                    />
                  </td>
                  <td>
                    <input
                      value={editForm().domain}
                      onInput={(e) => setEditForm((f) => ({ ...f, domain: e.currentTarget.value }))}
                    />
                  </td>
                  <td colspan={2}>
                    <input
                      type="password"
                      value={editPassword()}
                      onInput={(e) => setEditPassword(e.currentTarget.value)}
                      placeholder="Nouveau mot de passe (optionnel)"
                    />
                  </td>
                </tr>
                <tr class="tenant-volt__edit-actions-row">
                  <td colspan={6}>
                    <button class="btn btn--primary btn--xs" onClick={() => saveEdit(c.id)} disabled={saving()}>
                      {saving() ? "…" : "Enregistrer"}
                    </button>
                    <button class="btn btn--xs" onClick={() => setEditingId(null)}>
                      Annuler
                    </button>
                  </td>
                </tr>
              </Show>
            )}
          </For>
        </tbody>
      </table>
    </section>
  );
};
